/**
 * Cobrança (Invoice / Nota de Débito)
 *
 * O patrocinador não paga porque o valor está no sistema: ele precisa de um
 * documento para anexar no e-mail. Aqui a cobrança nasce de uma conta a
 * receber e sai em PDF no mesmo desenho da nota usada hoje.
 *
 * Os dados de quem cobra (emitente + banco) ficam salvos na configuração da
 * empresa na primeira emissão — ninguém quer redigitar CPF e PIX toda vez.
 */
import React, { useEffect, useState } from 'react';
import { X, FileDown, Plus, Trash2 } from 'lucide-react';

export interface ParteCobranca {
  nome: string;
  endereco: string;
  cidade: string;
  uf: string;
  cep: string;
  documento: string;
  documentoTipo?: string;
}

export interface DadosBancarios {
  banco: string;
  agencia: string;
  conta: string;
  pix: string;
}

export interface ItemCobranca {
  descricao: string;
  valor: number;
}

export interface PayloadCobranca {
  tipo: 'nd' | 'invoice';
  contaId?: string;
  emitidaEm: string;
  vencimento: string;
  formaPagamento: string;
  moeda: string;
  cliente: ParteCobranca;
  emitente: ParteCobranca;
  dadosBancarios: DadosBancarios;
  itens: ItemCobranca[];
  observacoes: string;
}

const PARTE_VAZIA: ParteCobranca = {
  nome: '', endereco: '', cidade: '', uf: '', cep: '', documento: '', documentoTipo: '',
};
const BANCO_VAZIO: DadosBancarios = { banco: '', agencia: '', conta: '', pix: '' };

const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const rotulo = 'block text-xs font-medium text-gray-600 mb-1';

/** Bloco de identificação de uma das partes (quem cobra / quem paga). */
const BlocoParte: React.FC<{
  titulo: string;
  valor: ParteCobranca;
  onChange: (p: ParteCobranca) => void;
}> = ({ titulo, valor, onChange }) => {
  const set = (campo: keyof ParteCobranca, v: string) => onChange({ ...valor, [campo]: v });
  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
      <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">{titulo}</p>
      <div>
        <label className={rotulo}>Razão social / nome *</label>
        <input className={input} value={valor.nome} onChange={e => set('nome', e.target.value)} />
      </div>
      <div>
        <label className={rotulo}>Endereço</label>
        <input className={input} value={valor.endereco} onChange={e => set('endereco', e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={rotulo}>Cidade</label>
          <input className={input} value={valor.cidade} onChange={e => set('cidade', e.target.value)} />
        </div>
        <div>
          <label className={rotulo}>UF</label>
          <input className={input} maxLength={2} value={valor.uf}
            onChange={e => set('uf', e.target.value.toUpperCase())} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={rotulo}>CEP</label>
          <input className={input} value={valor.cep} onChange={e => set('cep', e.target.value)} />
        </div>
        <div>
          <label className={rotulo}>CPF / CNPJ</label>
          <input className={input} value={valor.documento} onChange={e => set('documento', e.target.value)} />
        </div>
      </div>
    </div>
  );
};

export const CobrancaModal: React.FC<{
  /** Conta a receber que originou a cobrança (opcional: dá para emitir avulsa). */
  conta?: { _id?: string; descricao?: string; valor?: number; moeda?: string; vencimento?: string; cliente?: string } | null;
  emitentePadrao?: Partial<ParteCobranca>;
  bancoPadrao?: Partial<DadosBancarios>;
  clientesConhecidos?: ParteCobranca[];
  moedaBase: string;
  moedas: string[];
  onFechar: () => void;
  onEmitir: (p: PayloadCobranca) => Promise<void>;
}> = ({ conta, emitentePadrao, bancoPadrao, clientesConhecidos = [], moedaBase, moedas, onFechar, onEmitir }) => {
  const hoje = new Date().toISOString().slice(0, 10);

  const [tipo, setTipo] = useState<'nd' | 'invoice'>('nd');
  const [emitidaEm, setEmitidaEm] = useState(hoje);
  const [vencimento, setVencimento] = useState(conta?.vencimento || hoje);
  const [formaPagamento, setFormaPagamento] = useState('PIX');
  const [moeda, setMoeda] = useState(conta?.moeda || moedaBase);
  const [emitente, setEmitente] = useState<ParteCobranca>({ ...PARTE_VAZIA, ...(emitentePadrao || {}) });
  const [cliente, setCliente] = useState<ParteCobranca>({ ...PARTE_VAZIA, nome: conta?.cliente || '' });
  const [banco, setBanco] = useState<DadosBancarios>({ ...BANCO_VAZIO, ...(bancoPadrao || {}) });
  const [itens, setItens] = useState<ItemCobranca[]>([
    { descricao: conta?.descricao || '', valor: Number(conta?.valor || 0) },
  ]);
  const [observacoes, setObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  // Cliente já usado antes preenche o resto sozinho
  useEffect(() => {
    const conhecido = clientesConhecidos.find(c => c.nome && c.nome === cliente.nome);
    if (conhecido && !cliente.documento && !cliente.endereco) {
      setCliente({ ...PARTE_VAZIA, ...conhecido });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente.nome]);

  const total = itens.reduce((s, i) => s + (Number(i.valor) || 0), 0);
  const setItem = (idx: number, campo: keyof ItemCobranca, v: string) =>
    setItens(itens.map((i, n) => n === idx ? { ...i, [campo]: campo === 'valor' ? Number(v) || 0 : v } : i));

  const emitir = async () => {
    if (!emitente.nome.trim()) return setErro('Preencha quem está cobrando.');
    if (!cliente.nome.trim()) return setErro('Preencha o cliente.');
    if (!itens.some(i => i.descricao.trim() || i.valor > 0)) return setErro('Informe ao menos um item.');
    setErro('');
    setSalvando(true);
    try {
      await onEmitir({
        tipo, contaId: conta?._id, emitidaEm, vencimento, formaPagamento, moeda,
        cliente, emitente, dadosBancarios: banco,
        itens: itens.filter(i => i.descricao.trim() || i.valor > 0),
        observacoes,
      });
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível emitir a cobrança.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Emitir cobrança</h3>
            <p className="text-xs text-gray-500 mt-0.5">Gera o PDF para enviar ao pagador</p>
          </div>
          <button onClick={onFechar} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className={rotulo}>Documento</label>
              <select className={input} value={tipo} onChange={e => setTipo(e.target.value as 'nd' | 'invoice')}>
                <option value="nd">Nota de Débito</option>
                <option value="invoice">Invoice</option>
              </select>
            </div>
            <div>
              <label className={rotulo}>Emissão</label>
              <input type="date" className={input} value={emitidaEm} onChange={e => setEmitidaEm(e.target.value)} />
            </div>
            <div>
              <label className={rotulo}>Vencimento</label>
              <input type="date" className={input} value={vencimento} onChange={e => setVencimento(e.target.value)} />
            </div>
            <div>
              <label className={rotulo}>Moeda</label>
              <select className={input} value={moeda} onChange={e => setMoeda(e.target.value)}>
                {(moedas.length ? moedas : [moedaBase]).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <BlocoParte titulo="Quem cobra" valor={emitente} onChange={setEmitente} />
            <BlocoParte titulo="Cliente" valor={cliente} onChange={setCliente} />
          </div>

          {/* Descritivo */}
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Descritivo</p>
              <button onClick={() => setItens([...itens, { descricao: '', valor: 0 }])}
                className="px-2.5 py-1 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-1">
                <Plus className="w-3 h-3" /> Item
              </button>
            </div>
            <div className="space-y-2">
              {itens.map((item, idx) => (
                <div key={idx} className="flex gap-2">
                  <input className={input} placeholder="Descrição" value={item.descricao}
                    onChange={e => setItem(idx, 'descricao', e.target.value)} />
                  <input className={`${input} w-40`} type="number" step="0.01" placeholder="0,00" value={item.valor || ''}
                    onChange={e => setItem(idx, 'valor', e.target.value)} />
                  {itens.length > 1 && (
                    <button onClick={() => setItens(itens.filter((_, n) => n !== idx))}
                      className="p-2 text-red-400 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-3 pt-3 border-t border-gray-100">
              <span className="text-sm font-bold text-gray-900">
                Total: {moeda} {total.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Pagamento */}
          <div className="border border-gray-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Informações de pagamento</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className={rotulo}>Forma</label>
                <input className={input} value={formaPagamento} onChange={e => setFormaPagamento(e.target.value)} />
              </div>
              <div>
                <label className={rotulo}>Banco</label>
                <input className={input} value={banco.banco} onChange={e => setBanco({ ...banco, banco: e.target.value })} />
              </div>
              <div>
                <label className={rotulo}>Agência</label>
                <input className={input} value={banco.agencia} onChange={e => setBanco({ ...banco, agencia: e.target.value })} />
              </div>
              <div>
                <label className={rotulo}>Conta</label>
                <input className={input} value={banco.conta} onChange={e => setBanco({ ...banco, conta: e.target.value })} />
              </div>
            </div>
            <div>
              <label className={rotulo}>PIX</label>
              <input className={input} value={banco.pix} onChange={e => setBanco({ ...banco, pix: e.target.value })} />
            </div>
            <div>
              <label className={rotulo}>Observações (saem no rodapé)</label>
              <input className={input} value={observacoes} onChange={e => setObservacoes(e.target.value)} />
            </div>
          </div>

          {!!erro && <p className="text-sm text-red-600">{erro}</p>}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={onFechar} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">
            Cancelar
          </button>
          <button onClick={emitir} disabled={salvando}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
            <FileDown className="w-4 h-4" />
            {salvando ? 'Gerando...' : 'Emitir e baixar PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CobrancaModal;
